import {
  SubmissionAttachment,
  SubmissionPortal,
  SubmissionSummary,
  postSubmissionAttachmentPortal,
} from "@rubi/api-generated";
import axios from "axios";
import { ITheme, Model, Question, SvgRegistry } from "survey-core";

import { statusTypes } from "@/types/typesReportV2";

import { applyCondensedReportTitles } from "./condensedTitles";
import { defaultTheme } from "./constants";
import {
  applyViewVisibility,
  registerShowInProperty,
} from "./custom-questions/showInProperty";
import { extractLocaleStringFromField } from "./form-management/utils";
import { IPlace } from "./types";

export interface UploadFile {
  name: string;
  type: string;
  content: string;
  id: string;
  pdf_url?: string | null;
}

export const lastUpdatedBy = (
  submission: SubmissionSummary,
  includeBadge = true,
) => {
  const name = submission.last_updated_by
    ? `${submission.last_updated_by?.rank_short ?? ""} ${
        submission.last_updated_by?.first_name ?? ""
      } ${submission.last_updated_by?.last_name ?? ""}`
    : "Reporting Individual";

  if (includeBadge && submission.last_updated_by) {
    return `${name} (${submission.last_updated_by?.badge_number ?? ""})`;
  }
  return name;
};

export const assignedTo = (submission: SubmissionSummary) => {
  if (submission.assigned_to) {
    return `${submission.assigned_to?.first_name ?? ""} ${submission.assigned_to?.last_name ?? ""} (${submission.assigned_to?.badge_number ?? ""})`;
  }
  return "";
};

export const uploadToS3 = async (
  file: File,
  submissionID: string,
  versionSpecific = false,
) => {
  const s3Details = (
    await postSubmissionAttachmentPortal({
      path: { submission_id: submissionID },
      body: {
        filename: file.name,
        size: file.size,
        content_type: file.type,
        version_specific: versionSpecific,
      },
    })
  ).data!;
  const headers: { [key: string]: string } = {};
  s3Details.s3_presigned.headers
    .filter((header) => header.key !== "content-length")
    .forEach((header) => {
      headers[header.key] = header.value;
    });
  await axios.put(s3Details.s3_presigned.uri, file, {
    headers,
  });
  return s3Details;
};

export const imageUrlToBase64 = async (url) => {
  const data = await axios.get(url, {
    responseType: "blob",
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
      Expires: "0",
    },
  });
  const blob = data.data;
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => {
      const base64data = reader.result;
      resolve(base64data?.toString() ?? "");
    };
    reader.onerror = reject;
  });
};

export async function loadImageData(
  data: any,
  attachments: Array<SubmissionAttachment>,
  includeVideos = false,
  submissionID: string,
  surveyClass?: string | null,
  submissionTimeSubmitted?: string | null,
) {
  const assetURLs = attachments.map((attachment) => {
    return {
      name: attachment.filename,
      content: attachment.presigned_get.s3_presigned.uri,
      id: attachment.id,
      include_in_export: attachment.include_in_export,
      thumbnail_url:
        attachment.thumbnail_presigned_get?.s3_presigned?.uri ?? null,
      pdf_url: attachment.pdf_presigned_get?.s3_presigned?.uri ?? null,
      title: attachment.title ?? null,
      description: attachment.description ?? null,
      attachment_type: attachment.attachment_type ?? null,
    };
  });

  function traverse(jsonObj) {
    if (jsonObj !== null && typeof jsonObj == "object") {
      Object.entries(jsonObj).forEach(([key, value]) => {
        // key is either an array index or object key
        if (
          (key === "type" &&
            (jsonObj["type"]?.startsWith("image") ||
              jsonObj["type"]?.startsWith("application") ||
              (includeVideos && jsonObj["type"]?.startsWith("video")))) ||
          key === "file"
        ) {
          const assetURL = assetURLs.find(
            (attachment) => attachment.name === jsonObj.name,
          );
          if (key === "file" && !jsonObj["type"]) {
            jsonObj["type"] = "application/zip";
          }
          jsonObj["content"] = assetURL?.content ?? "";
          jsonObj["id"] = assetURL?.id ?? "";
          jsonObj["submission_id"] = submissionID ?? "";
          jsonObj["include_in_export"] = assetURL?.include_in_export ?? false;
          jsonObj["thumbnail_url"] = assetURL?.thumbnail_url ?? null;
          jsonObj["pdf_url"] = assetURL?.pdf_url ?? null;
          jsonObj["title"] = assetURL?.title ?? null;
          jsonObj["description"] = assetURL?.description ?? null;
          jsonObj["attachment_type"] = assetURL?.attachment_type ?? null;
          jsonObj["submission_survey_class"] = surveyClass ?? null;
          jsonObj["submission_time_submitted"] =
            submissionTimeSubmitted ?? null;
        } else {
          traverse(value);
        }
      });
    } else {
      // jsonObj is a number or string
    }
  }
  traverse(data);
}

export const extractAttachmentsFromObject = (
  obj,
  reportAttachments: {
    incident_evidence_description: string;
    incident_evidence_upload: UploadFile[];
  }[] = [],
) => {
  if (
    typeof obj === "object" &&
    obj !== null &&
    (obj.incident_evidences ||
      obj.vulnerable_evidences ||
      obj["person-vulnerable_evidences"])
  ) {
    const incidentEvidences: any[] =
      obj.incident_evidences ??
      obj.vulnerable_evidences ??
      obj["person-vulnerable_evidences"];
    incidentEvidences.forEach((evidence) => {
      reportAttachments.push({
        incident_evidence_description:
          evidence.incident_evidence_description ??
          evidence.incident_evidence_keywordDescription ??
          evidence["person-vulnerable_evidence-portrait_description"],
        incident_evidence_upload:
          evidence.incident_evidence_upload ??
          evidence["person-vulnerable_evidence-portrait_upload"],
      });
    });
  } else if (
    typeof obj === "object" &&
    obj !== null &&
    obj["bicycles-registered"]
  ) {
    const incidentEvidences: any[] = obj["bicycles-registered"];
    incidentEvidences.forEach((evidence) => {
      evidence["bicycle-registered_evidence_upload"]?.forEach((upload) => {
        reportAttachments.push({
          incident_evidence_description: "",
          incident_evidence_upload: [upload],
        });
      });
      evidence["bicycle-registered_evidence-proofOfOwnership_upload"]?.forEach(
        (upload) => {
          reportAttachments.push({
            incident_evidence_description: "",
            incident_evidence_upload: [upload],
          });
        },
      );
    });
  } else if (Array.isArray(obj)) {
    obj.forEach((item) => {
      extractAttachmentsFromObject(item, reportAttachments);
    });
  }
  return reportAttachments;
};

export const removeAttachmentsFromPages = (pages) => {
  if (Array.isArray(pages)) {
    pages.forEach((page) => {
      removeAttachmentsFromPages(page);
    });
  } else if (typeof pages === "object" && pages !== null) {
    if (pages.elements) {
      pages.elements = pages.elements.map((element) => {
        const elementsToRemove = element.templateElements?.filter(
          (templateElement) => templateElement.name.includes("_upload"),
        );

        // remove the _upload and _description elements
        elementsToRemove?.forEach((elementToRemove) => {
          const elementName = elementToRemove.name;
          const elementDescription = elementName.replace(
            "_upload",
            "_description",
          );
          element.templateElements = element.templateElements?.filter(
            (templateElement) =>
              templateElement.name !== elementName &&
              templateElement.name !== elementDescription,
          );
        });
        return element;
      });
    }
    Object.values(pages).forEach((value) => {
      removeAttachmentsFromPages(value);
    });
  }
};

export const isPrototypeKey = (key: string | number) =>
  key === "__proto__" || key === "constructor" || key === "prototype";

export const parseData = (obj, parseDates = false, printVersion = false) => {
  const dateKeys = [
    "incident_endDateTime",
    "incident_startDateTime",
    "supplementary_reportedDateTime",
    "supplementary_linkedDateTime",
  ];
  if (typeof obj === "object" && obj !== null && !Array.isArray(obj)) {
    if (
      printVersion &&
      obj.googleMapsPlace &&
      !Object.keys(obj).includes("cameraLocations")
    ) {
      return extractAddress(obj);
    }

    Object.keys(obj).forEach((key) => {
      if (isPrototypeKey(key)) return;
      if (parseDates && dateKeys.includes(key)) {
        const parsedDate = new Date(obj[key]).toLocaleString();
        if (parsedDate == "Invalid Date") {
          return;
        } else obj[key] = parsedDate;
      }
      if (key.includes("-Comment")) {
        const keyWithoutComment = key.split("-Comment")[0];
        const keyData = obj[keyWithoutComment];
        if (Array.isArray(keyData)) {
          const newKeyData = keyData.map((data) => {
            if (data === "other") {
              // replace the value with the value from the -Comment key
              return obj[key];
            }
            return data;
          });
          obj[keyWithoutComment] = newKeyData;
        } else if (typeof keyData === "string" && keyData === "other") {
          obj[keyWithoutComment] = obj[key];
        }
      }
      obj[key] = parseData(obj[key], parseDates, printVersion);
    });
  } else if (Array.isArray(obj)) {
    obj.forEach((item) => {
      item = parseData(item, parseDates, printVersion);
    });
  }
  // else if (typeof obj === "boolean") {
  //   return obj ? "Yes" : "No";
  // }
  return obj;
};

export const extractAddress = (value: any) => {
  if (value["googleMapsPlace"]) {
    const place = value.googleMapsPlace;
    const address = place?.formatted_address
      ? place.formatted_address?.includes(place.name ? place.name : "")
        ? place.formatted_address
        : place.name + " - " + place.formatted_address
      : "";
    return address;
  }
};

export type ReportAttachment = {
  incident_evidence_description: string;
  incident_evidence_upload: UploadFile[];
};

export const getAttachmentContent = (
  reportAttachments: ReportAttachment[] = [],
  attachments: SubmissionAttachment[],
) => {
  const processedIDs: string[] = [];

  reportAttachments.forEach((attachment) => {
    attachment.incident_evidence_upload?.forEach((uploadFile) => {
      const attachmentContent = attachments.find(
        (attachment) =>
          attachment.filename === uploadFile.name &&
          !processedIDs.includes(attachment.id),
      );
      if (attachmentContent) {
        uploadFile.content = attachmentContent.presigned_get.s3_presigned.uri;
        uploadFile.id = attachmentContent.id;
        processedIDs.push(attachmentContent.id);
      }
    });
  });
};

export const updateElementTypes = (element: Question) => {
  const newElement = { ...element } as Question;
  // if (newElement.type === "address-autocomplete") {
  //   newElement.type = "text";
  // }
  // else if (newElement.type === "boolean") {
  //   newElement.type = "text";
  // }
  const addressKeys = [
    "person-reportingParty_location-home",
    "person-reportingParty_location-home_address",
    "person-vulnerable_location_address",
    "business-reportingParty_location_address",
    "person-vulnerable_location-home_address",
    "incident_location_address",
    "person-keyholder_location-home",
    "person-involved_location-home",
    "person-witness_location-home",
    "person-suspect_location-home",
    "person-victim_location-home",
    "person-additionalVictim_location-home",
    "person-emergContact_location-home",
    "person-keyholder_location-work",
    "person-involved_location-work",
    "person-witness_location-work",
    "person-suspect_location-work",
    "person-victim_location-work",
    "person-additionalVictim_location-work",
    "person-emergContact_location-work",
    "person-suspect_location-associated_address",
    "person-involved_location-home_address",
    "person-involved_location-work_address",
  ];
  const isAddress = addressKeys.includes(newElement.name);

  if (newElement.type === "radiogroup") {
    newElement.type = "dropdown";
  } else if (newElement.type === "checkbox") {
    // Multi-select checkbox → tagbox (multi-select dropdown). Same array value
    // shape, so no data migration is needed — mirrors radiogroup → dropdown.
    newElement.type = "tagbox";
  } else if (isAddress) {
    newElement.type = "address-autocomplete";
  } else if (newElement.type === "file") {
    newElement.storeDataAsText = false;
  }

  if (newElement.type === "panel" && newElement.name === "eligibility-panel") {
    newElement.state = "collapsed";
  }

  ["elements", "templateElements"].forEach((key) => {
    if (newElement[key]) {
      newElement[key] = newElement[key].map(updateElementTypes);
    }
  });
  return newElement;
};

export const updatePrintElementTypes = (element: Question) => {
  const newElement = { ...element } as Question;
  if (
    newElement.type === "address-autocomplete" ||
    newElement.type === "text" ||
    newElement.type === "dropdown" ||
    newElement.type === "radiogroup" ||
    newElement.type === "tagbox"
  ) {
    newElement.type = "comment";
    newElement.rows = 1;
    newElement.autoGrow = true;
    newElement.minWidth = "auto";
  }
  ["elements", "templateElements"].forEach((key) => {
    if (newElement[key]) {
      newElement[key] = newElement[key].map(updatePrintElementTypes);
    }
  });
  return newElement;
};

const generateExtraPDFData = (reportMetadata: any, description: string) => {
  return {
    incident_type: description,
    report_status: statusTypes[reportMetadata.status]
      ? statusTypes[reportMetadata.status]
      : reportMetadata.status,
    report_trackingNumber: reportMetadata.tracking_number ?? "",
    report_agencyRMSNumber: reportMetadata.incident_number ?? "",
    report_reportedDateTime: reportMetadata.time_created
      ? new Date(reportMetadata.time_created).toLocaleString()
      : "'",
    report_approvedByName:
      lastUpdatedBy(reportMetadata as SubmissionSummary, false) ?? "",
    report_approvedByBadge: reportMetadata.last_updated_by?.badge_number ?? "",
    report_processedByName: lastUpdatedBy(
      reportMetadata as SubmissionSummary,
      false,
    ),
    report_processedByBadge: reportMetadata.last_updated_by?.badge_number ?? "",
    supplementary_reportedDateTime: reportMetadata.time_created
      ? new Date(reportMetadata.time_created).toLocaleString()
      : "'",
    supplementary_linkedDateTime: reportMetadata.time_updated
      ? new Date(reportMetadata.time_updated)
      : "'",
    report_officerNotes:
      reportMetadata?.report_officerNotes ??
      reportMetadata?.approve_note ??
      reportMetadata?.reject_note ??
      reportMetadata?.escalate_note ??
      "",
  };
};

// Replace SurveyJS's default +/− panel expand glyphs with a chevron-down. The
// same icon is used for both expand and collapse states; CSS in
// condensed-report.css rotates it -90° when the panel is collapsed so it
// reads as a right-pointing chevron. Idempotent — safe to call repeatedly.
let chevronIconsRegistered = false;
const registerCollapseChevronIcons = () => {
  if (chevronIconsRegistered) return;
  const chevronDown =
    '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M16.5275 10.5275L12.5275 14.5275C12.3775 14.6775 12.1875 14.7475 11.9975 14.7475C11.8075 14.7475 11.6175 14.6775 11.4675 14.5275L7.4675 10.5275C7.1775 10.2375 7.1775 9.7575 7.4675 9.4675C7.7575 9.1775 8.2375 9.1775 8.5275 9.4675L11.9975 12.9375L15.4675 9.4675C15.7575 9.1775 16.2375 9.1775 16.5275 9.4675C16.8175 9.7575 16.8175 10.2375 16.5275 10.5275Z"/></svg>';
  SvgRegistry.registerIconFromSvg("expand-16x16", chevronDown);
  SvgRegistry.registerIconFromSvg("collapse-16x16", chevronDown);
  chevronIconsRegistered = true;
};

export const condensedViewTheme: ITheme = {
  ...defaultTheme,
  cssVariables: {
    ...defaultTheme.cssVariables,
    // Element padding — zero so input fields sit flush against the panel
    // content inset (--cr-field-inset) instead of carrying an extra per-element
    // left/right gutter on top of it.
    "--sv-element-add-padding-left": "0px",
    "--sv-element-add-padding-right": "0px",

    // Corner radius
    "--sjs-corner-radius": "var(--cr-radius, 4px)",

    // Panel border - transparent
    "--sjs-border-default": "rgba(0, 0, 0, 0)",

    // Input field border
    "--sjs-shadow-inner": "0px 0px 0px 0px rgba(0, 0, 0, 0)",
    "--sjs-shadow-inner-reset": "0px 0px 0px 0px rgba(0, 0, 0, 0)",

    // Input field backgrounds
    "--sjs-editor-background": "transparent",
    "--sjs-editorpanel-backcolor": "transparent",
    "--sjs-general-backcolor-dim-light": "transparent",
    "--sjs-editorpanel-cornerRadius": "var(--cr-radius, 4px)",

    // General background colors — read the shared --cr-* gray tokens (defined in
    // condensed-report.css) so SurveyJS renders the same grays as the stylesheet.
    // The literal fallback applies when that stylesheet is not loaded.
    "--sjs-general-backcolor": "var(--cr-card-bg, rgba(255, 255, 255, 1))",
    "--sjs-question-background": "var(--cr-card-bg, rgba(255, 255, 255, 1))",

    // Font family — align the SurveyJS-rendered report body with MUI's default
    // Roboto stack so report labels/values match the surrounding chrome and the
    // right-hand notes panel (SurveyJS otherwise defaults to its bundled "Open
    // Sans"). Reads the shared --cr-font-family token (condensed-report.css);
    // the literal fallback applies when that stylesheet isn't loaded.
    "--font-family":
      'var(--cr-font-family, "Roboto", "Helvetica", "Arial", sans-serif)',
    "--sjs-font-family":
      'var(--cr-font-family, "Roboto", "Helvetica", "Arial", sans-serif)',

    // Base font size (other sizes cascade from this)
    "--sjs-font-size": "14px",

    // Survey title
    "--sjs-font-surveytitle-size": "16px",

    // Survey description (preserved from existing)
    "--sjs-font-surveydescription-size": "12px",

    // Page title
    "--sjs-font-pagetitle-size": "16px",

    // Question title — bold uppercase label in the secondary gray (MUI
    // text.secondary), matching an MUI field label above its value.
    "--sjs-font-questiontitle-size": "10px",
    "--sjs-font-questiontitle-weight": "700",
    "--sjs-font-questiontitle-color":
      "var(--cr-label-color, rgba(0, 0, 0, 0.6))",

    // Descriptions are the report's helper text (question/survey/page
    // descriptions, rendered as .sd-description). SurveyJS otherwise leaves
    // them at its default light gray; map them to the shared helper color so
    // they match the panel helper text (NotesSidePanel / ShareReportContent /
    // EmailNotesCard footer).
    "--sjs-font-questiondescription-color":
      "var(--cr-helper-color, rgba(0, 0, 0, 0.6))",
    "--sjs-font-surveydescription-color":
      "var(--cr-helper-color, rgba(0, 0, 0, 0.6))",
    "--sjs-font-pagedescription-color":
      "var(--cr-helper-color, rgba(0, 0, 0, 0.6))",

    // Answers/input values (editor font) — read the shared value-text token so
    // every editor (text inputs, dropdown values, tagbox chips) renders the same
    // primary gray (MUI text.primary). Empty/placeholder text uses the lighter
    // placeholder token below.
    "--sjs-font-editorfont-size": "16px",
    "--sjs-font-editorfont-color": "var(--cr-value-color, rgba(0, 0, 0, 0.87))",
    "--sjs-font-editorfont-weight": "400",
    "--sjs-font-editorfont-placeholdercolor":
      "var(--cr-placeholder-color, rgba(0, 0, 0, 0.42))",

    // Base unit
    "--sjs-base-unit": "8px",
  },
};

/**
 * Show an em-dash ("—") placeholder for unanswered fields so empty values read
 * as "no value" rather than blank space. Applied to text, comment, email,
 * dropdown, and tagbox (skipped for booleans/files/panels where a dash would
 * confuse). SurveyJS renders inputs as `disabled` in display mode — which
 * suppresses the placeholder — so the post-render hook flips empty text inputs
 * to `readonly` instead. Shared by the main condensed model and the
 * reviewer-details model so the em-dash standard is identical across both.
 * `includeNested` (3rd getAllQuestions arg) reaches dropdowns inside
 * PanelDynamic panels and matrix cells.
 */
export const applyCondensedEmDashPlaceholders = (model: Model): void => {
  const PLACEHOLDER_TYPES = new Set([
    "text",
    "comment",
    "email",
    "dropdown",
    "tagbox",
  ]);
  model.getAllQuestions(false, false, true).forEach((q: any) => {
    if (PLACEHOLDER_TYPES.has(q.getType?.())) {
      q.placeholder = "—";
    }
  });
  model.onAfterRenderQuestion.add((sender: any, options: any) => {
    const q = options.question;
    const t = q?.getType?.();
    if (t !== "text" && t !== "comment" && t !== "email") return;
    const root: HTMLElement | undefined = options.htmlElement;
    if (!root) return;
    // In edit mode, strip any readonly attribute set during a prior
    // display-mode render — leaving it would block typing.
    if (sender.mode !== "display") {
      root.querySelectorAll("input, textarea").forEach((el) => {
        (el as HTMLInputElement | HTMLTextAreaElement).removeAttribute(
          "readonly",
        );
      });
      return;
    }
    if (!q.isEmpty?.()) return;
    root.querySelectorAll("input, textarea").forEach((el) => {
      const input = el as HTMLInputElement | HTMLTextAreaElement;
      input.removeAttribute("disabled");
      input.setAttribute("readonly", "");
      if (!input.placeholder) input.placeholder = "—";
    });
  });
};

export const REDACTED_VALUE = "***";

const PARTNER_REDACTED_FIELDS = new Set([
  "person-reportingParty_dob",
  "person-reportingParty_location-home",
  "person-reportingParty_location-home_address",
]);

const getRedactedQuestions = (model: Model): any[] =>
  model
    .getAllQuestions()
    .filter(
      (q: any) =>
        PARTNER_REDACTED_FIELDS.has(q.getValueName()) &&
        q.value === REDACTED_VALUE,
    );

export const applyRedactedFieldDisplay = (
  model: Model,
  notice: string,
): void => {
  const redacted = new Set(getRedactedQuestions(model));
  redacted.forEach((q) => {
    if (q.getType?.() === "text") {
      q.inputType = "text";
      q.maskType = "none";
    }
    q.value = notice;
  });
  model.onAfterRenderQuestion.add((_sender: any, options: any) => {
    const root: HTMLElement | undefined = options.htmlElement;
    if (!root || !redacted.has(options.question)) return;
    root.classList.add("cr-redacted");
    root.querySelectorAll("input, textarea").forEach((el) => {
      el.removeAttribute("disabled");
      el.setAttribute("readonly", "");
    });
  });
};

/**
 * Build the SurveyJS model for the condensed report's reviewer-details section
 * (the optional sign-off form some surveys configure via
 * `reviewer_details_survey`). Applies the same condensed theme, padding reset,
 * and title normalization the section renders with, so the production view and
 * the Storybook story share one construction path and can't drift.
 *
 * The caller owns data persistence (e.g. debounced auto-save via
 * `onValueChanged`) and any later mode changes.
 */
export const buildReviewerDetailsModel = (
  reviewerDetailsSurvey: any,
  {
    data,
    mode = "edit",
  }: { data?: Record<string, any> | null; mode?: "display" | "edit" } = {},
): Model => {
  const model = new Model(reviewerDetailsSurvey);
  model.mode = mode;
  if (data) {
    model.data = data;
  }
  model.applyTheme(condensedViewTheme);
  model.css = {
    body: {
      paddingLeft: 0,
      paddingRight: 0,
    },
    boolean: {
      itemDisabled: {},
      itemHover: {},
    },
    page: {
      root: {
        paddingLeft: 0,
        paddingRight: 0,
      },
    },
  };
  applyCondensedReportTitles(model);
  applyCondensedEmDashPlaceholders(model);
  return model;
};

export const createCondensedModel = async (
  basePDFStructure: any,
  data: any,
  attachments: SubmissionAttachment[],
  hideAdminInformation = true,
  isSupplementary = false,
  printVersion = false,
  submissionID: string,
  surveyClass?: string | null,
  submissionTimeSubmitted?: string | null,
) => {
  registerShowInProperty();
  registerCollapseChevronIcons();

  const basePDFStructureCopy: any = {
    ...basePDFStructure,
    headerView: false,
    autoGrowComment: true,
    allowResizeComment: false,
    width: "960px",
  };
  const supplementaryAdminInformationName =
    "ADMINISTRATIVE SUPPLEMENTARY INFORMATION";
  const originalReportAdminInformationName = "Administrative Information";

  basePDFStructureCopy.pages = basePDFStructureCopy.pages.map((page) => ({
    ...page,
    elements: page.elements.map(updateElementTypes),
  }));

  if (hideAdminInformation) {
    basePDFStructureCopy.pages = basePDFStructureCopy.pages.map((page) => ({
      ...page,
      elements: page.elements.filter((element) => {
        return (
          !extractLocaleStringFromField(element.title)
            ?.toLowerCase()
            .includes(supplementaryAdminInformationName.toLowerCase()) &&
          !extractLocaleStringFromField(element.title)
            ?.toLowerCase()
            .includes(originalReportAdminInformationName.toLowerCase())
        );
      }),
    }));
  } else {
    if (isSupplementary) {
      basePDFStructureCopy.pages = basePDFStructureCopy.pages.map((page) => ({
        ...page,
        elements: page.elements.filter(
          (element) =>
            !element.title
              ?.toLowerCase()
              .includes(originalReportAdminInformationName.toLowerCase()),
        ),
      }));
    }
  }

  const tempData = { ...data };

  // let reportAttachments: ReportAttachment[] = [];

  // extractAttachmentsFromObject(tempData, reportAttachments);
  parseData(tempData, !hideAdminInformation, printVersion);

  // if (basePDFStructure["description"]?.includes("Bicycle Registration")) {
  await loadImageData(
    tempData,
    attachments,
    true,
    submissionID,
    surveyClass,
    submissionTimeSubmitted,
  );
  // } else {
  //   removeAttachmentsFromPages(basePDFStructureCopy.pages);
  //   getAttachmentContent(reportAttachments, attachments);
  // }

  // reportAttachments = reportAttachments.filter(
  //   (attachment) => attachment.incident_evidence_upload?.length > 0,
  // );

  // convert datetime-local to text if PDF rendering
  // Clone rendering is not parsing the datetime correctly
  const modelJSON = basePDFStructureCopy;

  // if (!hideAdminInformation) {
  //   modelJSON = JSON.parse(
  //     JSON.stringify(basePDFStructureCopy).replaceAll("datetime-local", "text"),
  //   );
  // }
  const newSurvey = new Model(modelJSON);
  newSurvey.mode = "display";
  newSurvey.showNavigationButtons = false;
  newSurvey.showTitle = false;
  newSurvey.logo = "";
  newSurvey.checkErrorsMode = "onValueChanged";

  applyViewVisibility(newSurvey, "condensed");

  newSurvey.applyTheme(condensedViewTheme);
  // const customStyles = {
  //   "sliderGhost": {
  //     color: "red",
  //     fontSize: "18px",
  //     backgroundColor: "red"
  //   }
  // };

  // use the .css to get the properties of the survey
  // console.log(newSurvey.css);
  // const prevCss = newSurvey.css;
  newSurvey.css = {
    body: {
      paddingLeft: 0,
      paddingRight: 0,
    },
    boolean: {
      itemDisabled: {},
      itemHover: {},
    },
    page: {
      root: {
        paddingLeft: 0,
        paddingRight: 0,
      },
    },
  };

  if (!isSupplementary) {
    newSurvey.data = {
      ...tempData,
      ...generateExtraPDFData(tempData, basePDFStructure.description),
    };
  } else {
    parseData(tempData, !hideAdminInformation, printVersion);
    newSurvey.data = tempData;
  }

  // Em-dash placeholder for unanswered fields (shared with the reviewer-details
  // model so the standard stays identical).
  applyCondensedEmDashPlaceholders(newSurvey);

  // On screen, let each question shrink to its column. SurveyJS sets an inline
  // min-width (default ~300px) that would force a field wider than a narrow
  // container and overlap the next column; 0 lets long values truncate with an
  // ellipsis (see condensed-report.css) instead. Print keeps the default
  // min-width for its fixed page width. includeNested (3rd arg) reaches fields
  // inside PanelDynamic panels and matrix cells.
  if (!printVersion) {
    newSurvey.getAllQuestions(false, false, true).forEach((q: any) => {
      q.minWidth = "0px";
    });
  }

  /* Numeric-masked text questions (WEIGHT, AGE, quantities) inherit an inline
     text-align: right from SurveyJS's numeric input mask — an inline style no
     stylesheet can override. Setting inputTextAlignment to a non-"auto" value
     makes the model emit text-align: left instead, so every condensed field
     reads left. includeNested reaches PanelDynamic and matrix fields. */
  newSurvey.getAllQuestions(false, false, true).forEach((q: any) => {
    if (q.inputTextAlignment !== undefined) {
      q.inputTextAlignment = "left";
    }
    q.items?.forEach?.((item: any) => {
      if (item.inputTextAlignment !== undefined) {
        item.inputTextAlignment = "left";
      }
    });
  });

  // Title-case authored section headers (regular Panels and PanelDynamic outer
  // headers like "INVOLVED ITEM(S)") and blank out untitled PanelDynamics so
  // SurveyJS doesn't fall back to rendering their internal name.
  applyCondensedReportTitles(newSurvey);

  // Make section panels (and PanelDynamic outer headers) collapsible, open
  // by default. SurveyJS renders a chevron in the title bar once `state`
  // is set to "expanded" or "collapsed"; "default" leaves the panel
  // non-interactive.
  newSurvey.getAllPanels().forEach((p: any) => {
    // Keep the eligibility section collapsed by default (set above); expand
    // all other section panels so they open by default.
    if (p.name !== "eligibility-panel") {
      p.state = "expanded";
    }
  });
  newSurvey.getAllQuestions().forEach((q: any) => {
    if (q.getType?.() === "paneldynamic") {
      q.state = "expanded";
    }
  });

  return { newSurvey, reportAttachments: [] };
};

export const transformPlaceObject = (
  place: google.maps.places.PlaceResult,
): IPlace => {
  const lat = place.geometry?.location?.lat() ?? 0;
  const lng = place.geometry?.location?.lng() ?? 0;

  return {
    address_components: place.address_components
      ? [...place.address_components]
      : [],
    formatted_address: place.formatted_address ? place.formatted_address : "",
    geometry: { lat, lng },
    name: place.name ? place.name : "",
    url: place.url ? place.url : "",
    types: place.types ? [...place.types] : [],
    place_id: place.place_id ? place.place_id : "",
  };
};

export const getTitleFromSubmission = (
  submissionData: {
    submission?: unknown;
    first_name?: string | null;
    last_name?: string | null;
  },
  surveyClass: string,
) => {
  const submission = submissionData.submission as { [key: string]: any };

  if (!submission) {
    return "";
  }

  if (surveyClass.toLowerCase().includes("keyholder")) {
    return submission["business-reportingParty_name"];
  }
  if (surveyClass.toLowerCase().includes("vulnerable person")) {
    return (
      submission["person-vulnerable_firstName"] +
      " " +
      submission["person-vulnerable_lastName"]
    );
  }
  if (surveyClass.toLowerCase().includes("bicycle")) {
    return (
      submission["person-reportingParty_firstName"] +
      " " +
      submission["person-reportingParty_lastName"]
    );
  }

  if (surveyClass.toLowerCase().includes("camera")) {
    if (submission["business-reportingParty_name"]) {
      return submission["business-reportingParty_name"];
    }
    return submissionData.first_name + " " + submissionData.last_name;
  }
  return surveyClass;
};

export const getLocationsFromSubmission = (
  submissionData: { [key: string]: any },
  surveyClass: string,
) => {
  const submission = submissionData.submission as { [key: string]: any };

  if (!submission) {
    return [];
  }

  if (surveyClass.toLowerCase().includes("keyholder")) {
    return [submission["business-reportingParty_location_address"]];
  }
  if (surveyClass.toLowerCase().includes("vulnerable person")) {
    return submission["person-vulnerable_locations"].map(
      (location) => location["person-vulnerable_location_address"],
    );
  }
  if (surveyClass.toLowerCase().includes("bicycle")) {
    return [submission["person-reportingParty_location-home_address"]];
  }
  if (surveyClass.toLowerCase().includes("camera")) {
    return [submission["location-registered_address"]];
  }

  // General fallback address keys
  if (submission["location-registered_address"]) {
    return [submission["location-registered_address"]];
  }
  if (submission["incident_location_address"]) {
    return [submission["incident_location_address"]];
  }
  return [];
};
